/**
 * Build an Error from a failed response. The server returns either
 * `{ message }` or `{ error }`, but the body may also be non-JSON
 * (e.g. an IIS error page), so never let JSON parsing mask the real error.
 * Extra fields from the body (e.g. `retryAfterSeconds`) are kept on `error.body`.
 * @param {Response} response
 * @param {string} fallbackMessage
 * @returns {Promise<Error>} Error with `status` and `body` attached.
 */
export const buildResponseError = async (response, fallbackMessage) => {
  let body = null;
  try {
    const text = await response.text();
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }

  const error = new Error(body?.message || body?.error || fallbackMessage);
  error.status = response.status;
  error.body = body;
  return error;
};

/**
 * fetch + JSON with consistent error handling.
 * @param {string} url
 * @param {RequestInit & { json?: any }} [options] `json` is sent as the JSON body.
 * @param {string} [fallbackMessage]
 */
export const requestJson = async (url, options = {}, fallbackMessage = 'Request failed') => {
  const { json, headers, ...rest } = options;
  const response = await fetch(url, {
    ...rest,
    headers: {
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
  });

  if (!response.ok) {
    throw await buildResponseError(response, fallbackMessage);
  }
  return response.json();
};
